<template>
  <div class="kpi-grid">
    <!-- KPI ① 监控覆盖率 -->
    <div
      class="kpi kpi-1 kpi-click"
      title="点击 → 查看全部监控主体 / Clique → ver todas as entidades monitoradas"
      @click="$emit('jump', { module: 'ie-list', status: null, tip: '已跳转「IE 信息管理」' })"
    >
      <div class="ico-wrap">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      </div>
      <div class="kpi-main">
        <bi-label class="label" zh="监控覆盖率" pt="Cobertura de Monitoramento" />
        <div class="value" :style="coverage < totalUfs ? { color: '#F53F3F' } : {}">{{ coverage }} / {{ totalUfs }}</div>
        <div class="sub">
          <template v-if="missingUf.length">
            缺 {{ missingUf.length }} 州：{{ missingUf.join('、') }}
            <span class="sub-pt">Faltam {{ missingUf.length }} estados: {{ missingUf.join(', ') }}</span>
          </template>
          <template v-else>
            已服务州全部纳入监控
            <span class="sub-pt">Todos os estados atendidos estão sob monitoramento</span>
            <span class="pct-ok">↑ {{ percent }}%</span>
          </template>
        </div>
      </div>
    </div>

    <!-- KPI ② 异常主体数 -->
    <div
      class="kpi kpi-2 kpi-click"
      title="点击 → 查看异常主体清单 / Clique → ver lista de entidades com exceção"
      @click="$emit('jump', { module: 'ie-list', status: 3, tip: `已筛选「异常」主体：${anomalyCount} 条` })"
    >
      <div class="ico-wrap">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0z" />
          <path d="M12 9.2v4" />
          <circle
            cx="12"
            cy="16.4"
            r="1.1"
            fill="currentColor"
            stroke="none"
          />
        </svg>
      </div>
      <div class="kpi-main">
        <bi-label class="label" zh="异常主体数" pt="Entidades com Exceção" />
        <div class="value">{{ anomalyCount }}</div>
        <div class="sub">
          点击卡片筛选异常主体
          <span class="sub-pt">Clique no cartão para filtrar as entidades com exceção</span>
          <span class="pct-up">›</span>
        </div>
      </div>
    </div>

    <!-- KPI ③ 今日告警次数 -->
    <div
      class="kpi kpi-3 kpi-click"
      title="点击 → 查看告警日志 / Clique → ver log de alertas"
      @click="$emit('jump', { module: 'alert-log', tip: '已跳转「告警日志」' })"
    >
      <div class="ico-wrap">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
      </div>
      <div class="kpi-main">
        <bi-label class="label" zh="今日告警次数" pt="Alertas de Hoje" />
        <div class="value">{{ alertTotal }}</div>
        <div class="sub">
          {{ alertTypeText }}
          <span class="sub-pt">{{ alertTypeTextPt }}</span>
        </div>
      </div>
    </div>

    <!-- KPI ④ 上次检查时间 -->
    <div
      class="kpi kpi-4"
      :class="{ 'is-delayed': checkDelayed }"
      :title="checkDelayed ? '距上次检查已超过 30 分钟，请确认定时任务是否正常触发' : ''"
    >
      <div class="ico-wrap">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      </div>
      <div class="kpi-main">
        <bi-label class="label" zh="上次检查时间" pt="Última Verificação" />
        <div class="value is-time" :style="checkDelayed ? { color: '#F53F3F' } : {}">{{ fmtTime(schedule.at) }}</div>
        <div class="sub">
          下次定时：{{ fmtTime(schedule.nextAt) }}
          <span class="sub-pt">Próxima execução: {{ fmtTime(schedule.nextAt) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BiLabel from './BiLabel';

export default {
  name: 'KpiCards',
  components: { BiLabel },
  props: {
    // 州状态（字段同地图接口 items：uf / covered / enabled / tax / sys / overall 等）
    states: { type: Array, default: () => [] },
    // 今日告警（字段同 KPI 接口 alertToday），接口未通时的兜底
    alertToday: { type: Object, default: () => ({}) },
    // 上次检查（字段同 KPI 接口 lastCheck：at / source / nextAt / stale）
    schedule: { type: Object, default: () => ({}) },
    serviceTotal: { type: Number, default: 0 },
    // KPI 接口（/monitor/dashboard/summary）的返回体；传入后优先用它渲染，未传入时沿用本地计算
    summary: { type: Object, default: null }
  },
  computed: {
    /** 已纳入监控的州：covered=有启用主体 且 enabled=纳入监控 */
    coveredStates() {
      return this.states.filter(s => s.covered && s.enabled !== false);
    },
    /** 覆盖率分母：优先取 KPI 接口的 totalUfs（已服务州总数），否则用本地常量 */
    totalUfs() {
      if (this.summary && this.summary.coverage && this.summary.coverage.totalUfs) {
        return this.summary.coverage.totalUfs;
      }
      return this.serviceTotal;
    },
    /** 覆盖率分子：优先取 KPI 接口的 coveredUfs，否则按本地数据计算 */
    coverage() {
      if (this.summary && this.summary.coverage) return this.summary.coverage.coveredUfs;
      return this.coveredStates.length;
    },
    /** 未纳入监控的州 UF 列表（KPI 副文案里提示"缺哪几州"） */
    missingUf() {
      if (this.summary) return this.summary.excludedUfs || [];
      return this.states.filter(s => !this.coveredStates.includes(s)).map(s => s.uf);
    },
    /** 覆盖率百分比：优先取接口的 rate（≤1 视为比例需乘 100），否则本地计算 */
    percent() {
      if (this.summary && this.summary.coverage && typeof this.summary.coverage.rate === 'number') {
        const rate = this.summary.coverage.rate;
        return rate > 0 && rate <= 1 ? Math.round(rate * 100) : Math.round(rate);
      }
      if (!this.totalUfs) return 0;
      return Math.round((this.coverage / this.totalUfs) * 100);
    },
    /** 异常主体数：优先取接口的 anomaly.count，否则税务无效或系统未配置的州计入 */
    anomalyCount() {
      if (this.summary && this.summary.anomaly) return this.summary.anomaly.count;
      // tax=2（税务失效）/ sys=2（系统未配置）计入；枚举可能返回字符串，统一转数字再比较
      return this.states.filter(s => Number(s.tax) === 2 || Number(s.sys) === 2).length;
    },
    /** 告警总条数（KPI ③ 主数值）：优先取接口的 alertToday.total */
    alertTotal() {
      if (this.summary && this.summary.alertToday) return this.summary.alertToday.total;
      return this.alertToday.total || 0;
    },
    /** 中文：紧急 2 · 警告 2 · 恢复 1（与原型短称一致） */
    alertTypeText() {
      const t = this.summary && this.summary.alertToday ? this.summary.alertToday : this.alertToday;
      const parts = [];
      if (t.urgent) parts.push(`紧急 ${t.urgent}`);
      if (t.warn) parts.push(`警告 ${t.warn}`);
      if (t.recover) parts.push(`恢复 ${t.recover}`);
      return parts.length ? parts.join(' · ') : '暂无告警';
    },
    /** 葡语：Críticos 2 · Avisos 2 · Recuperações 1 */
    alertTypeTextPt() {
      const t = this.summary && this.summary.alertToday ? this.summary.alertToday : this.alertToday;
      const parts = [];
      if (t.urgent) parts.push(`Críticos ${t.urgent}`);
      if (t.warn) parts.push(`Avisos ${t.warn}`);
      if (t.recover) parts.push(`Recuperações ${t.recover}`);
      return parts.length ? parts.join(' · ') : 'Nenhum alerta';
    },
    /** 距上次检查超过 30 分钟视为未按时触发 */
    checkDelayed() {
      return this.checkDelayMinutes > 30;
    },
    /** 距上次检查的分钟数：lastCheck.at 为 ISO 时间，可直接参与时间差计算 */
    checkDelayMinutes() {
      const at = this.schedule && this.schedule.at;
      if (!at) return 0;
      const d = new Date(at);
      if (isNaN(d.getTime())) return 0;
      return Math.floor((Date.now() - d.getTime()) / 60000);
    }
  },
  methods: {
    /** ISO 时间 → MM-DD HH:mm:ss（KPI ④ 上次 / 下次检查时间展示） */
    fmtTime(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    }
  }
};
</script>

<style scoped lang="scss">
/* 与原型 kpi-grid / kpi 尺寸严格对齐 */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .kpi .label,
  .kpi .value {
    white-space: nowrap;
  }
}
.kpi {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.kpi .ico-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}
.kpi.kpi-1 .ico-wrap { background: #e8f7f0; color: #00a551; }
.kpi.kpi-2 .ico-wrap { background: #ffecec; color: #f53f3f; }
.kpi.kpi-3 .ico-wrap { background: #e6f0ff; color: #165dff; }
.kpi.kpi-4 .ico-wrap { background: #fff7e6; color: #ff7d00; }
.kpi-4.is-delayed .ico-wrap { background: #ffecec; color: #f53f3f; }

.kpi-main {
  min-width: 0;
  flex: 1 1 auto;
}
.kpi .label {
  color: #86909c;
  font-size: 12px;
}
.kpi .value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}
.kpi .value.is-time {
  font-size: 16px;
}
.kpi .sub {
  color: #86909c;
  font-size: 12px;
  margin-top: 2px;
  line-height: 1.5;
}
.kpi .sub-pt {
  display: block;
  font-size: 12px;
  color: #86909c;
}
.kpi .pct-ok {
  color: #00b42a;
  font-size: 12px;
}
.kpi .pct-up {
  color: #f53f3f;
  font-size: 12px;
}

/* KPI 可点击态（原型 v4.0） */
.kpi-click {
  cursor: pointer;
  position: relative;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.kpi-click:hover {
  border-color: #00a551;
  box-shadow: 0 4px 12px rgba(0, 168, 107, 0.14);
  transform: translateY(-1px);
}
.kpi-click:active {
  transform: translateY(0);
}
.kpi-click::after {
  content: '›';
  position: absolute;
  top: 10px;
  right: 14px;
  font-size: 18px;
  line-height: 1;
  color: #c9cdd4;
  transition: color 0.15s, transform 0.15s;
}
.kpi-click:hover::after {
  color: #00a551;
  transform: translateX(3px);
}
</style>
